import { useState, useEffect, useCallback } from "react";
import { Heart, GraduationCap, AlertCircle, CheckCircle, Mail, Calendar, FileText, MapPin, Award, Sparkles } from "lucide-react";
import axios from "axios";
import logger from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Scholarship = () => {
  const [scholarshipOpen, setScholarshipOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/scholarship/status`);
      setScholarshipOpen(response.data.scholarship_open);
    } catch (error) {
      logger.error("Failed to check scholarship status:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Keiah White Memorial Scholarship | Soar Solutions Youth Center";
    checkStatus();
  }, [checkStatus]);

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="scholarship-success">
        <div className="text-center px-6">
          <CheckCircle className="w-20 h-20 text-soar-teal mx-auto mb-6" />
          <h2 className="font-heading text-4xl text-soar-blue mb-4">Application Submitted</h2>
          <p className="text-soar-blue/60">Thank you. We will be in touch soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="scholarship-page" className="bg-white">

      {/* Hero - Magazine Style */}
      <section className="relative min-h-[80vh] flex items-center">
        <div className="absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_soaryouth/artifacts/waafm39m_DSC_5218.jpeg')] bg-cover bg-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-5 h-5 text-soar-gold" />
              <span className="text-soar-gold font-bold text-sm uppercase tracking-widest">Memorial Scholarship</span>
            </div>
            
            <h1 className="font-heading text-7xl sm:text-8xl text-soar-blue leading-[0.9] mb-6">
              Keiah<br />White
            </h1>
            
            <p className="font-accent text-4xl text-soar-teal mb-6">"A light that lifted others."</p>
            
            <p className="text-soar-blue/60 text-lg leading-relaxed mb-8 max-w-md">
              Honoring students who embody compassion, kindness, and love for others.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <div className="bg-soar-gold text-soar-blue px-5 py-2 rounded-full font-bold text-sm">
                Class of 2025
              </div>
              {loading ? (
                <div className="animate-spin w-5 h-5 border-2 border-soar-teal border-t-transparent rounded-full" />
              ) : scholarshipOpen ? (
                <div className="flex items-center gap-2 text-soar-teal font-semibold text-sm">
                  <CheckCircle className="w-4 h-4" /> Applications Open
                </div>
              ) : (
                <div className="flex items-center gap-2 text-soar-blue/50 font-semibold text-sm">
                  <Calendar className="w-4 h-4" /> Opens Dec 1, 2026
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quote Banner */}
      <section className="bg-soar-blue py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-white/80 text-lg leading-relaxed">
            Keiah was always a friend to those seeking one. Her generosity and love for others were at the core of who she was. She made people feel <span className="text-soar-gold font-semibold">seen and valued</span>.
          </p>
        </div>
      </section>

      {/* Requirements - Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-5xl text-soar-blue">Who Can Apply?</h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: GraduationCap, label: "High School Senior", sub: "Class of 2027", bg: "bg-soar-teal" },
              { icon: MapPin, label: "Kern County", sub: "California Resident", bg: "bg-soar-gold" },
              { icon: Award, label: "3.0+ GPA", sub: "Cumulative", bg: "bg-soar-teal" },
              { icon: FileText, label: "Essay", sub: "500 Words", bg: "bg-soar-gold" },
            ].map((item, i) => (
              <div key={i} className="group relative overflow-hidden rounded-3xl bg-white border-2 border-soar-blue/5 p-8 hover:border-soar-teal/30 transition-all hover:-translate-y-1 hover:shadow-xl">
                <div className={`w-14 h-14 ${item.bg} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading text-xl text-soar-blue mb-1">{item.label}</h3>
                <p className="text-soar-blue/50 text-sm">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Essay Section */}
      <section className="py-16 bg-gradient-to-b from-soar-teal/5 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-[2rem] shadow-xl border border-soar-blue/5 overflow-hidden">
            <div className="bg-soar-teal p-6 text-center">
              <h2 className="font-heading text-3xl text-white">Essay Prompt</h2>
            </div>
            <div className="p-8 sm:p-12">
              <p className="text-soar-blue/80 text-xl italic leading-relaxed text-center mb-8">
                "Describe how you have generously encouraged and shown love for others with a helping hand, your presence, or a deed. Share specific examples of how you have made others feel seen and valued, just as Keiah did throughout her life."
              </p>
              
              <div className="flex flex-wrap justify-center gap-3">
                {["500 words", "Double-spaced", "Times New Roman 12pt", "PDF format"].map((tag) => (
                  <span key={tag} className="bg-soar-blue/5 text-soar-blue/70 px-4 py-2 rounded-full text-sm font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Apply Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-soar-gold/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-soar-teal/10 rounded-full blur-[80px]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <div className="bg-soar-gold rounded-[2rem] p-10 sm:p-14 text-center">
            <GraduationCap className="w-16 h-16 text-soar-blue mx-auto mb-6" />
            <h2 className="font-heading text-5xl text-soar-blue mb-4">Ready to Apply?</h2>
            <p className="text-soar-blue/70 text-lg mb-2">Honor Keiah&apos;s legacy by sharing your story of compassion.</p>
            <p className="text-soar-blue font-bold text-lg mb-8">Applications open December 1, 2026</p>
            
            <a
              href="mailto:Scholarship@soar-solutions.org"
              className="inline-flex items-center gap-3 bg-soar-blue text-white px-10 py-5 rounded-full text-xl font-bold hover:bg-soar-blue/90 hover:scale-105 transition-all shadow-2xl"
              data-testid="scholarship-email-link"
            >
              <Mail className="w-6 h-6" /> Scholarship@soar-solutions.org
            </a>

            <div className="mt-10 bg-soar-blue/10 rounded-2xl p-6 text-left max-w-md mx-auto">
              <p className="text-soar-blue/70 text-sm font-bold mb-4 text-center uppercase tracking-wider">Application Checklist</p>
              <div className="space-y-3">
                {[
                  "Personal information & contact details",
                  "High school name & GPA",
                  "College plans & intended major",
                  "500-word compassion essay (PDF)",
                  "High School Transcripts"
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-soar-teal flex-shrink-0 mt-0.5" />
                    <span className="text-soar-blue/80">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Memorial Footer */}
      <section className="py-16 bg-soar-blue">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Heart className="w-10 h-10 text-soar-gold mx-auto mb-6" />
          <p className="font-accent text-4xl text-soar-gold mb-4">"A light that lifted others."</p>
          <p className="text-white/40">Keiah White &bull; Class of 2025 &bull; Forever in our hearts</p>
        </div>
      </section>
    </div>
  );
};

export default Scholarship;
